import { useState, useEffect } from "react";
import Select from "react-select";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import FormInput from "../../components/CustomComponents/Input/formInput";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import { CountrySelectionDropDown } from "../../components/CustomComponents/DropdownSelections/CountrySelectionDropDown";
import useDarkMode from "../../hooks/useDarkMode";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";

function PaymentProviderModal({
  isModalOpen,
  setIsModalOpen,
  onPaymentProviderSubmit,
  paymentProviderToEdit = null,
  isBulkEdit = false,
}) {
  const [providerData, setProviderData] = useState({
    name: "",
    countryCode: null,
    active: true,
  });

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  useEffect(() => {
    if (paymentProviderToEdit && !isBulkEdit) {
      setProviderData({
        id: paymentProviderToEdit.id,
        name: paymentProviderToEdit.name || "",
        countryCode: paymentProviderToEdit.countryCode
          ? { value: paymentProviderToEdit.countryCode, label: paymentProviderToEdit.countryCode }
          : null,
        active: paymentProviderToEdit.active !== undefined ? paymentProviderToEdit.active : true,
      });
    } else if (isBulkEdit && Array.isArray(paymentProviderToEdit)) {
      // For bulk edit, set empty fields
      setProviderData({
        name: "",
        countryCode: null,
        active: true,
      });
    }
  }, [paymentProviderToEdit, isBulkEdit]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setProviderData({
      name: "",
      countryCode: null,
      active: true,
    });
  };

  const handleSubmit = () => {
    try {
      // Validation
      if (!isBulkEdit) {
        if (!providerData.name || providerData.name.trim().length === 0) {
          showErrorToast("Please enter payment provider name");
          return;
        }

        if (!providerData.countryCode || !providerData.countryCode.value) {
          showErrorToast("Please select country");
          return;
        }
      }

      if (isBulkEdit) {
        // For bulk edit, only send fields that are filled
        const bulkUpdateData = {
          providerIds: paymentProviderToEdit.map((p) => p.id),
        };

        if (providerData.name && providerData.name.trim().length > 0) {
          bulkUpdateData.name = providerData.name.trim();
        }

        if (providerData.countryCode && providerData.countryCode.value) {
          bulkUpdateData.countryCode = providerData.countryCode.value;
        }

        bulkUpdateData.active = providerData.active;

        onPaymentProviderSubmit(bulkUpdateData, true, true);
      } else {
        const submitData = {
          ID: paymentProviderToEdit ? paymentProviderToEdit.id : 0,
          Name: providerData.name.trim(),
          CountryCode: providerData.countryCode.value,
          Active: providerData.active,
        };

        onPaymentProviderSubmit(submitData, !!paymentProviderToEdit, false);
      }

      handleCloseModal();
    } catch (err) {
      showErrorToast("Something went wrong.");
    }
  };

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      onSubmit={handleSubmit}
      title={
        isBulkEdit
          ? `Update ${paymentProviderToEdit?.length} Online Payment Methods`
          : paymentProviderToEdit
          ? "Update Online Payment Method"
          : "Add Online Payment Method"
      }
      subtitle={
        isBulkEdit
          ? "Update multiple online payment methods at once. Leave fields empty to keep existing values."
          : "Manage online payment method information"
      }
      showButton={true}
      isCentered={true}
      submitButtonText={isBulkEdit ? "Update All" : paymentProviderToEdit ? "Update" : "Add"}
    >
      <div className="row g-3 mb-4">
        <div className="col-md-12">
          <FormInput
            label="Payment Provider Name"
            placeHolder={isBulkEdit ? "(Optional) Leave empty to keep existing" : "Enter payment provider name"}
            type="text"
            value={providerData.name}
            onchange={(e) =>
              setProviderData({
                ...providerData,
                name: e.target.value,
              })
            }
            required={!isBulkEdit}
          />
        </div>

        <div className="col-md-12">
          <label className="form-label">
            Country Code {!isBulkEdit && <span className="text-danger">*</span>}
          </label>
          <CountrySelectionDropDown
            defaultValue={providerData.countryCode}
            onChange={(selectedOption) =>
              setProviderData({
                ...providerData,
                countryCode: selectedOption,
              })
            }
            styles={dropdownStyles}
            placeholder={isBulkEdit ? "(Optional) Leave empty to keep existing" : "Select Country"}
          />
        </div>

        <div className="col-md-12">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="activeSwitch"
              checked={providerData.active}
              onChange={(e) =>
                setProviderData({
                  ...providerData,
                  active: e.target.checked,
                })
              }
            />
            <label className="form-check-label" htmlFor="activeSwitch">
              Active
            </label>
          </div>
        </div>

        {isBulkEdit && (
          <div className="col-md-12">
            <div className="alert alert-info mb-0">
              <i className="fa fa-info-circle me-2"></i>
              <small>
                Note: Only filled fields will be updated for selected Online Payment Methods.
              </small>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
}

export default PaymentProviderModal;

